/**
 * @fileoverview Manual list-marker normalisation (MOR-977).
 *
 * Word letters imported into a precedent template often carry "fake" lists:
 * paragraphs with a hanging indent whose marker — "(a)", "(iii)", "1." — is
 * plain text separated from the clause body by a tab-turned-run of
 * non-breaking spaces. Those letters are justified, and browsers stretch the
 * space run when justifying a line — by a different amount on every line —
 * so the text after each marker starts at a different x position and the
 * points "won't line up" no matter how the user indents or re-justifies
 * (WLPR, Trading VA Director letter).
 *
 * `normalizeManualListMarkers` rewrites the marker + gap at the start of
 * each hanging-indent paragraph into a fixed-width inline-block box:
 *
 *   <p style="margin-left:42.55pt;text-indent:-21.3pt">
 *     (iii)&nbsp;&nbsp;&nbsp;&nbsp;engaged in conduct …
 *   </p>
 *     ↓
 *   <p style="margin-left:42.55pt;text-indent:-21.3pt">
 *     <span data-type="list-marker"
 *           style="display:inline-block;min-width:21.3pt;text-indent:0">(iii)</span>engaged in conduct …
 *   </p>
 *
 * The box's `min-width` equals the hanging distance (-text-indent), so the
 * clause text always starts exactly at the paragraph's `margin-left` — the
 * Word tab-stop model — regardless of marker width or how far justification
 * stretches the rest of the line (an inline-block's contents are never
 * stretched). `text-indent:0` is required: text-indent inherits into
 * inline-block boxes and would drag the marker out of its box.
 *
 * Only paragraphs that already encode Word's hanging-indent intent
 * (`text-indent` < 0 on the paragraph) are touched, so ordinary content
 * renders identically and only the tab-aligned case snaps to intent.
 *
 * The editor round-trips the box via the ListMarker TipTap node
 * (`list-marker-extension.ts`); the Word export rewrites it back to a real
 * tab (`prepareForWord`). Everything else (preview, PDF print, generated
 * Day 1 letters) renders the inline styles as-is.
 */

/** `data-type` value shared with the ListMarker TipTap node + Word export. */
export const LIST_MARKER_TYPE = "list-marker";

/**
 * Marker shapes recognised at the start of a hanging paragraph: "(a)",
 * "(iii)", "(2B)", "a)", "iv)", "1.", "A." — up to 5 alphanumerics wrapped
 * or trailed by a paren, or trailed by a dot. An optional leading
 * typographic quote (the s590 extract opens `"(a)`) stays inside the box so
 * it hangs with the marker. The gap after the marker is a tab or a run of
 * 2+ whitespace characters (`\s` covers `&nbsp;`), or nothing when the
 * marker ends its text node and the gap lives in a following node.
 */
const MARKER_TOKEN_RE =
  /^([“"]?(?:\([0-9a-zA-Z]{1,5}\)|[0-9a-zA-Z]{1,5}[.)]))(\t|\s{2,}|\s*$)/;

/** Negative `text-indent` (the hanging distance) read off a style attribute. */
const HANGING_INDENT_RE =
  /(?:^|;)\s*text-indent\s*:\s*-\s*(\d*\.?\d+)(pt|px|pc|in|cm|mm|em|rem)?\s*(?=;|$)/i;

/** Build the marker box's inline style for a given hanging distance. */
export function listMarkerBoxStyle(hang: string): string {
  return `display:inline-block;min-width:${hang};text-indent:0`;
}

/**
 * The first node inside `p` that renders visible content, walking depth-first
 * through wrapper elements (font/size spans etc.) and over whitespace-only
 * text. Returns the Text node when the paragraph opens with text, or null
 * when it opens with a non-text element (image, break, field chip, existing
 * marker box, …).
 */
function firstContentTextNode(p: Element): Text | null {
  let node: Node | null = p.firstChild;
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node as Text;
      if ((text.data ?? "").trim() !== "") return text;
      node = text.nextSibling;
      continue;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      if (
        el.getAttribute("data-type") ||
        el.tagName === "BR" ||
        el.tagName === "IMG"
      ) {
        return null;
      }
      if (el.firstChild) {
        node = el.firstChild;
        continue;
      }
    }
    node = node.nextSibling;
  }
  return null;
}

/** Text rendered by everything after `node` inside `p` (siblings + ancestors' siblings). */
function textAfter(node: Node, p: Element): string {
  let out = "";
  let current: Node | null = node;
  while (current && current !== p) {
    for (let s = current.nextSibling; s; s = s.nextSibling) {
      out += s.textContent ?? "";
    }
    current = current.parentNode;
  }
  return out;
}

/**
 * Rewrite manual `marker + gap` prefixes of hanging-indent paragraphs into
 * fixed-width list-marker boxes. Pure string → string; requires a DOM
 * (`DOMParser`) and returns the input unchanged in non-browser contexts or
 * when nothing matches. Idempotent — paragraphs already carrying a
 * `data-type="list-marker"` box are skipped.
 */
export function normalizeManualListMarkers(html: string): string {
  if (typeof DOMParser === "undefined") return html;
  if (!html || !/text-indent\s*:\s*-/i.test(html)) return html;

  const doc = new DOMParser().parseFromString(
    `<!doctype html><html><body>${html}</body></html>`,
    "text/html",
  );

  const changedElements = Array.from(
    doc.body.querySelectorAll("p[style]"),
  ).filter((p) => {
    const hangMatch = HANGING_INDENT_RE.exec(p.getAttribute("style") ?? "");
    if (!hangMatch) return false;
    if (p.querySelector(`[data-type="${LIST_MARKER_TYPE}"]`)) return false;

    const textNode = firstContentTextNode(p);
    if (!textNode) return false;
    const data = textNode.data ?? "";
    const tokenMatch = MARKER_TOKEN_RE.exec(data.replace(/^\s+/, ""));
    if (!tokenMatch) return false;
    const [matched, marker, gap] = tokenMatch;
    const leading = data.length - data.replace(/^\s+/, "").length;
    const remainder = data.slice(leading + matched.length);

    if (remainder.trim() === "") {
      // Marker (and maybe gap) ends this text node. Only proceed when the
      // clause text follows in a later node — a bare "(iii)" paragraph is
      // not a list item — and when a gap exists somewhere: in this node, or
      // as a whitespace-only next sibling (e.g. an mso-tab span).
      const rest = textAfter(textNode, p);
      if (rest.trim() === "") return false;
      const sibling = textNode.nextSibling;
      const siblingIsGap =
        sibling !== null &&
        (sibling.textContent ?? "") !== "" &&
        (sibling.textContent ?? "").trim() === "" &&
        !(
          sibling.nodeType === Node.ELEMENT_NODE &&
          (sibling as Element).getAttribute("data-type")
        );
      if (gap.length < 1 && !siblingIsGap) return false;
      if (siblingIsGap) sibling.parentNode?.removeChild(sibling);
    } else if (gap !== "\t" && gap.length < 2) {
      return false;
    }

    const hang = `${hangMatch[1]}${hangMatch[2] ?? "px"}`;
    const box = doc.createElement("span");
    box.setAttribute("data-type", LIST_MARKER_TYPE);
    box.setAttribute("style", listMarkerBoxStyle(hang));
    box.textContent = marker;

    const parent = textNode.parentNode;
    if (!parent) return false;
    parent.insertBefore(box, textNode);
    if (remainder.trim() !== "") {
      textNode.data = remainder;
    } else {
      parent.removeChild(textNode);
    }
    return true;
  });

  return changedElements.length > 0 ? doc.body.innerHTML : html;
}
